import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Dimmer, Form, Loader, MenuItem, Popup, Progress, Tab, TabPane } from 'semantic-ui-react';
import SurveyService from '~/api/services/survey';
import Box from '~/components/Box';
import Layout from '~/components/Layout'
import notify from '~/components/Notify';
import { RouteTypes } from '~/routes/constants';
import { StyledTabWrapper } from './style';
import { Controller, useForm } from 'react-hook-form';
import { FormFields } from '~/components/FormFields';
import { getError } from '~/components/FormFields/util';
import { StyledRating } from '~/pages/Dairy/Survey/ShowSurvey/CreateEditQuestion/QuestionForms/Stars';
import Slider from './AnswerForms/Slider';
import GroupedQuestions from './AnswerForms/GroupedQuestions';
import CustomIcon from '~/components/CustomIcon';
import cloneDeep from 'lodash.clonedeep'
import Sorting from './AnswerForms/Sorting';
import MultiSelect from './AnswerForms/MultiSelect';
import { El } from '~/components/Layout/style';
import { formatNumber, formatNumberOneDecimal, formatNumbers } from '~/utils/helpers';
import MultiText from './AnswerForms/MultiText';

const NUMBER_OF_DISPLAYED_QUESTIONS = 10

interface RequestEntriesProps {
  edit?: boolean
}

const RequestEntries: React.FC<RequestEntriesProps> = (props) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [questionFilter, setQuestionFilter] = useState({
    page: 0,
    rows: NUMBER_OF_DISPLAYED_QUESTIONS,
    changeActiveIndex: true
  })
  const [surveyData, setSurveyData] = useState(null)
  const [loadingQuestionsCount, setLoadingQuestionsCount] = useState(true)
  const [totalQuestionsNo, setTotalQuestionsNo] = useState(0)
  const [questions, setQuestions] = useState([])
  const [editForm, setEditForm] = useState(false)
  const [indexOfLastCorrectAnswer, setIndexOfLastCorrectAnswer] = useState(0)
  const [isBusy, setIsBusy] = useState(false);
  const [activeIndex, setActiveIndex] = useState<string | number>(0)
  const {
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
    formState: { errors, isSubmitted },
    setError,
    clearErrors
  } = useForm({
    mode: 'onSubmit'
  })
  const { page, rows } = questionFilter

  useEffect(() => {
    fetchQuestionsCount()
  }, [])

  async function fetchQuestionsCount() {
    setLoadingQuestionsCount(true)
    try {
      const responseAnsweredCheck = await SurveyService.checkIfAlreadyAnswered(id)
      if (!responseAnsweredCheck.data?.answerAvailable) {
        notify(t('Survey has expired'), 'info')
        navigate(RouteTypes.SURVEY)
        return
      } else if (responseAnsweredCheck.data?.answered && !location.pathname.includes('edit')) {
        notify(t('Responses to the survey have been provided already.'), 'info')
        navigate(RouteTypes.SURVEY)
        return
      }
      const responseData = await SurveyService.getSurveysQuestionsData(id)
      if (responseData.data?.completed) {
        const response = await SurveyService.editSurveysQuestions(id)
        reset({ questions: [...response.data] })
        if (!editForm) {
          setEditForm(true)
        }
      }
      const response = await SurveyService.getNumberOfSurveyQuestions(id)
      setTotalQuestionsNo(response.data)
      setSurveyData(responseData.data)
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setLoadingQuestionsCount(false)
  }

  useEffect(() => {
    if (!loadingQuestionsCount) {
      fetchData()
    }
  }, [questionFilter, loadingQuestionsCount])

  function handleGroupDataStructure(question) {
    const groupQuestions = question?.groupQuestions.map(question => ({
      id: question.id,
      answers: []
    }))
    return {
      id: question.id,
      groupQuestions,
      questionInfo: {
        type: question?.type,
        required: question.required,
        groupQuestions: question?.groupQuestions
      }
    }
  }

  function handleAnswerStructure(question, { ...fields }, checkboxType = false) {
    return {
      id: question.id,
      answers: checkboxType ? [] : [{ ...fields }],
      questionInfo: {
        type: question?.type,
        required: question.required
      }
    }
  }

  function handleSortingStructure(question, value) {
    return {
      id: question.id,
      answers: [...value.map(answer => ({ answerId: answer.id, text: answer.answer }))],
      questionInfo: {
        type: question?.type,
        required: question.required
      }
    }
  }

  function handleAnswerArrayStructure(question, fields) {
    return {
      id: question.id,
      answers: [...fields],
      questionInfo: {
        type: question?.type,
        required: question.required
      }
    }
  }

  function getQuestionDataStructure(question) {
    switch (question.type) {
      case 'text':
        return handleAnswerStructure(question, { customText: '' })
      case 'radio':
        return handleAnswerStructure(question, { answerId: '' })
      case 'select':
        return handleAnswerStructure(question, { answerId: '' })
      case 'number':
        return handleAnswerStructure(question, { value: '' })
      case 'stars':
        return handleAnswerStructure(question, { value: '' })
      case 'slider':
        return handleAnswerStructure(question, { value: '', value2: question?.maxValue })
      case 'checkbox':
        return handleAnswerStructure(question, null, true)
      case 'group':
        return handleGroupDataStructure(question)
      case 'sorting':
        return handleSortingStructure(question, [...question.answers])
      case 'multiselect':
        return handleAnswerArrayStructure(question, [])
      case 'multitext':
        return handleAnswerStructure(question, { customText: '' })
      default:
        return
    }
  }

  async function fetchData() {
    setIsBusy(true)
    try {
      const response = await SurveyService.getFarmerQuestions(id, { ...questionFilter })
      const questionData = getQuestionDataStructure(response.data[0])
      const previousQuestions = cloneDeep(getValues('questions') || [])
      if (questionFilter.changeActiveIndex) {
        const typeIndex = typeof (questionFilter.changeActiveIndex) === 'boolean' ? 0 : questionFilter.changeActiveIndex
        setActiveIndex(typeIndex)
      }
      if (previousQuestions?.length <= NUMBER_OF_DISPLAYED_QUESTIONS * page) {
        reset({
          questions: [...previousQuestions, questionData]
        })
      }
      setQuestions(response.data)
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setIsBusy(false)
  }

  function getNumbersDecimal(type) {
    switch (type) {
      case 'whole_number':
        return 0
      case 'one_decimal_number':
        return 1
      case 'two_decimals_number':
        return 2
      default:
        return
    }
  }

  function formatNumbersBasedOnType(question) {
    const { minValue, maxValue } = question
    switch (question.subtype) {
      case 'whole_number':
        return { minValue: parseInt(minValue), maxValue: parseInt(maxValue) }
      case 'one_decimal_number':
        return { minValue: formatNumberOneDecimal(minValue), maxValue: formatNumberOneDecimal(maxValue) }
      case 'two_decimals_number':
        return { minValue: formatNumber(minValue), maxValue: formatNumber(maxValue) }
      default:
        return { minValue: 0, maxValue: 0 }
    }
  }

  function getNumberLabel(question) {
    const { minValue, maxValue } = formatNumbersBasedOnType(question)

    return `${question?.question} (${minValue} ${question?.unitLabel || ''} - ${maxValue} ${question?.unitLabel || ''})`
  }

  function renderQuestion(question) {
    const radioSingleErrorMessage = t('Please check one of the options')
    const checkboxMessage = t('Please enter at least {{min}} values or no more then {{max}}', { min: question?.requiredMin, max: question?.requiredMax })
    const indexByPage = parseInt(activeIndex.toString()) + page * rows
    switch (question.type) {
      case 'text':
        return <FormFields.Input
          control={control}
          rules={{
            required: question?.required ? t('Please enter text field') : null,
            maxLength: {
              value: 2000,
              message: t('Max. number of characters is 2000')
            }
          }}
          label={question?.question}
          placeholder={question?.placeholder}
          name={`questions[${indexByPage}].answers[0].customText`}
        />
      case 'radio':
        return <>
          <Controller
            control={control}
            name={`questions[${indexByPage}].answers[0].answerId`}
            rules={{
              required: {
                value: Boolean(question?.required),
                message: radioSingleErrorMessage
              }
            }}
            defaultValue=''
            render={({ field, fieldState }) => {
              return <>
                <Form.Field required={Boolean(question.required)}>
                  <label>{question?.question}</label>
                </Form.Field>
                {question?.answers?.map((answer, index) => (
                  <Form.Checkbox
                    key={answer.id}
                    error={fieldState.error && index === question?.answers.length - 1 ? getError(fieldState) : false}
                    label={answer.answer}
                    value={answer.id}
                    checked={field.value === answer.id}
                    onChange={(e, { value }) => setValue(`questions[${indexByPage}].answers[0].answerId`, value)}
                    radio
                  />
                ))}
              </>
            }}
          />
        </>
      case 'select':
        return <FormFields.Select
          control={control}
          name={`questions[${indexByPage}].answers[0].answerId`}
          label={question?.question}
          required={Boolean(question?.required)}
          placeholder={question?.placeholder}
          options={question?.answers?.map(anwser => ({ text: anwser?.answer, value: anwser?.id }))}
        />
      case 'number':
        return <FormFields.Input
          control={control}
          rules={{
            required: question?.required ? t('Please enter number') : null,
            validate: (v) => {
              //refactor this, numbers from input are not the same format as api return type is also not a number but string
              const valueFormatted = formatNumbers(v.toString().replace(',', '.'), question?.subtype).toString().replace(',', '.')
              const minValueFormatted = formatNumbers(question?.minValue, question?.subtype).toString().replace(',', '.')
              const maxValueFormatted = formatNumbers(question?.maxValue, question?.subtype).toString().replace(',', '.')
              if (parseFloat(valueFormatted) < parseFloat(minValueFormatted)) {
                return t('Min value is {{number}}', { number: minValueFormatted })
              } else if (parseFloat(valueFormatted) > parseFloat(maxValueFormatted)) {
                return t('Max value is {{number}}', { number: maxValueFormatted })
              }
              return true
            }
          }}
          allowDecimals={question?.subtype !== 'whole_number'}
          decimalScale={getNumbersDecimal(question?.subtype)}
          typeInput={'controled'}
          label={getNumberLabel(question)}
          placeholder={question?.placeholder}
          name={`questions[${indexByPage}].answers[0].value`}
        />
      case 'stars':
        return <FormFields.External
          control={control}
          rules={{
            required: question?.required ? t('Please select stars') : null
          }}
          label={question?.question}
          placeholder={question?.placeholder}
          name={`questions[${indexByPage}].answers[0].value`}
        >
          <StyledRating
            icon='star'
            defaultRating={getValues(`questions[${indexByPage}].answers[0].value`) || 0}
            maxRating={question?.value}
            onRate={(e, data) => setValue(`questions[${indexByPage}].answers[0].value`, data.rating)}
            size={'large'}
          />
        </FormFields.External>
      case 'slider':
        return <Slider
          control={control}
          activeIndex={indexByPage}
          question={question}
          setValue={setValue}
          getValues={getValues}
        />
      case 'group':
        return <GroupedQuestions
          control={control}
          activeIndex={indexByPage}
          getValues={getValues}
          isSubmitted={isSubmitted}
          clearErrors={clearErrors}
          setValue={setValue}
          question={question}
        />
      case 'sorting':

        return <Sorting
          control={control}
          activeIndex={indexByPage}
          setValue={setValue}
          question={question}
        />
      case 'multiselect':
        return <MultiSelect
          control={control}
          setValue={setValue}
          indexByPage={indexByPage}
          question={question}
          setError={setError}
          isSubmitted={isSubmitted}
          clearErrors={clearErrors}
        />
      case 'multitext':
        return <MultiText
          control={control}
          indexByPage={indexByPage}
          question={question}
          getValues={getValues}
        />
      case 'checkbox':
        return <Controller
          control={control}
          name={`questions[${indexByPage}].answers`}
          rules={{
            validate: (value) => (value?.length >= question?.requiredMin && value?.length <= question?.requiredMax) || checkboxMessage
          }}
          defaultValue=''
          render={({ field, fieldState }) => {
            return <>
              <Form.Field required={Boolean(question.required)}>
                <label>{question?.question}</label>
              </Form.Field>
              {question?.answers?.map((answer, index) => {
                return <Form.Checkbox
                  key={answer.id}
                  error={fieldState.error && index === question?.answers.length - 1 ? getError(fieldState) : false}
                  label={answer.answer}
                  value={answer.id}
                  checked={field?.value?.find(answerValue => answerValue?.answerId === answer.id)}
                  onChange={(e, data) => {
                    if (data.checked) {
                      const array = getValues(`questions[${indexByPage}].answers`)
                      const checkedItems = [...array, { answerId: data.value }]
                      setValue(`questions[${indexByPage}].answers`, checkedItems)
                      if (checkedItems.length >= question?.requiredMin && checkedItems.length <= question?.requiredMax) {
                        clearErrors(`questions[${indexByPage}].answers`)
                      }
                    } else {
                      const array = cloneDeep(field.value)
                      const indexItem = array.findIndex(item => item.answerId === data.value)
                      array.splice(indexItem, 1)
                      setValue(`questions[${indexByPage}].answers`, array)
                      if (array.length >= question?.requiredMin && array.length <= question?.requiredMax) {
                        clearErrors(`questions[${indexByPage}].answers`)
                      }
                    }
                  }}
                />
              })}
            </>
          }}
        />
      default:
        return
    }
  }

  function getFormStateError(index) {
    const questionErrors = errors?.questions;
    //@ts-ignore
    return index > questionErrors?.findIndex(item => !!item)
  }

  function getDisableClass(active, indexByPage, index) {
    if (editForm) {
      return getFormStateError(index) ? 'disable' : ''
    }
    return !active && indexByPage > indexOfLastCorrectAnswer ? 'disable' : ''
  }

  function getSuccessClass(active, indexByPage, index) {
    if (active) {
      return ''
    }
    if (editForm) {
      return !getFormStateError(index) ? 'success' : ''
    }
    return !active && indexByPage < indexOfLastCorrectAnswer ? 'success' : ''
  }

  function getMenuItem(question, index) {
    const indexByPage = index + page * rows
    const active = activeIndex === index
    const disable = getDisableClass(active, indexByPage, index)
    const success = getSuccessClass(active, indexByPage, index)
    return <>
      {(index + 1) % NUMBER_OF_DISPLAYED_QUESTIONS === 1 && <div className='arrow-left'>
        <CustomIcon
          onClick={() => setQuestionFilter({ ...questionFilter, page: page - 1, changeActiveIndex: true })}
          name='arrow alternate circle left'
          disabled={page === 0}
        />
      </div>
      }
      <MenuItem
        key={question?.id}
        className={`tab-item ${active ? 'active' : ''}`}
        disabled={editForm ? disable === 'disable' : index > indexOfLastCorrectAnswer}
        onClick={() => setActiveIndex(index)}
      >
        <Popup
          content={question?.question}
          trigger={
            <div className={`tab-number ${disable} ${success}`}>
              {index + (page * NUMBER_OF_DISPLAYED_QUESTIONS) + 1}
            </div>}
        />
        {(index + 1) % NUMBER_OF_DISPLAYED_QUESTIONS === 0 && <div className='arrow-right'>
          <CustomIcon
            onClick={() => {
              const lastAnsweredQuestion = getValues('questions')?.length - 1
              //@ts-ignore
              setQuestionFilter({ ...questionFilter, page: page + 1, changeActiveIndex: lastAnsweredQuestion % NUMBER_OF_DISPLAYED_QUESTIONS })
            }}
            name='arrow alternate circle right'
            disabled={editForm ? disable === 'disable' : indexOfLastCorrectAnswer / NUMBER_OF_DISPLAYED_QUESTIONS <= page + 1}
          />
        </div>}
      </MenuItem>
    </>
  }

  function transformPayloadForApi(data) {
    data?.questions.forEach((question, index) => {
      const type = editForm ? question.type : question?.questionInfo?.type
      const required = editForm ? Boolean(question?.required) : question.questionInfo.required
      switch (type) {
        case 'group':
          if (!required) {
            /*             data.questions[index]?.groupQuestions?.forEach((groupQuestion, indexAnswer) => {
              if (!groupQuestion?.id) {
                delete data.questions[index].groupQuestions[indexAnswer].id = question?.questionInfo.groupQuestions[indexAnswer].id
              }
            }) */
          }
          delete question?.questionInfo
          break;
        case 'slider':
        case 'stars':
        case 'number':
          if (!required) {
            if (!question.answers[0]?.value || question.answers[0]?.value === '') {
              data.questions[index].answers = []
            } else {
              data.questions[index].answers[0].value = data.questions[index].answers[0]?.value.toString().replace(',', '.')
            }
          } else {
            data.questions[index].answers[0].value = data.questions[index].answers[0]?.value?.toString()?.replace(',', '.')
          }
          delete question?.questionInfo
          break;
        case 'text':
          if (!required) {
            if (!question.answers[0]?.customText || question.answers[0]?.customText === '') {
              data.questions[index].answers = []
            }
          }
          delete question?.questionInfo
          break;
        case 'radio':
        case 'select':
          if (!required) {
            if (!question.answers[0]?.answerId || question.answers[0]?.answerId === '') {
              data.questions[index].answers = []
            }
          }
          delete question?.questionInfo
          break;
        case 'multiselect':
        case 'checkbox':
          if (!required) {
            question.answers.map((answer, indexAnswer) => {
              if (!answer?.answerId || answer?.answerId === '') {
                data.questions[index].answers.splice(indexAnswer, 1)
              }
            })
          }
          delete question?.questionInfo
          break;
        case 'multitext':
          data.questions[index].answers = question.answers.filter((answer) => !!answer?.customText)
          delete question?.questionInfo
          break;
        default:
          delete question?.questionInfo
      }
    })
    return data
  }

  async function handleCheckIfSurveyIsActive() {
    try {
      const responseAnsweredCheck = await SurveyService.checkIfAlreadyAnswered(id)
      if (!responseAnsweredCheck.data?.answerAvailable) {
        notify(t('Survey has expired'), 'info')
        navigate(RouteTypes.SURVEY)
        return
      } else if (responseAnsweredCheck.data?.answered && !location.pathname.includes('edit')) {
        notify(t('Responses to the survey have been provided already.'), 'info')
        navigate(RouteTypes.SURVEY)
        return
      }
    } catch (error) {
      throw ({
        response: {
          data: {
            message: t('Error while checking is survey active')
          }
        }
      })
    }
  }

  async function handleNextPage(data) {
    setIsBusy(true)
    try {
      if (data?.questions?.length === totalQuestionsNo) {
        await handleCheckIfSurveyIsActive()
        const payload = transformPayloadForApi(data)
        await SurveyService.storeSurveyAnswers(id, payload)
        notify(t('Successfully answered survey questions'), 'success')
        navigate(RouteTypes.SURVEY)
      } else {
        setQuestionFilter({ ...questionFilter, page: page + 1, changeActiveIndex: true })
        return
      }
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setIsBusy(false)
  }

  function handleAddNextQuestion(questionsFormData, indexByPage) {
    const questionsForm = cloneDeep(questionsFormData)
    if (!questionsForm[parseInt(activeIndex.toString()) + 1 + indexByPage]) {
      const question = questions[parseInt(activeIndex.toString()) + 1]
      questionsForm[parseInt(activeIndex.toString()) + 1 + indexByPage] = getQuestionDataStructure(question)
    }
    return questionsForm
  }

  async function handleEditSubmit(data) {
    setLoadingQuestionsCount(true)
    try {
      const responseAnsweredCheck = await SurveyService.checkIfAlreadyAnswered(id)
      if (!responseAnsweredCheck.data?.answerAvailable) {
        notify(t('Survey has expired'), 'info')
        navigate(RouteTypes.SURVEY)
        return
      }
      await SurveyService.updateSurveysQuestions(id, data)
      notify(t('Succesfully updated survey answers'), 'success')
      navigate(RouteTypes.SURVEY)
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setLoadingQuestionsCount(false)
  }

  async function onSubmit(data) {
    if (editForm) {
      const payload = transformPayloadForApi(data)
      handleEditSubmit(payload)
      return
    }

    setIsBusy(true)
    if ((parseInt(activeIndex.toString()) + 1 + page * rows) > indexOfLastCorrectAnswer) {
      setIndexOfLastCorrectAnswer(parseInt(activeIndex.toString()) + 1 + page * rows)
    }
    //@ts-ignore
    if (activeIndex + 1 === questions.length) {
      await handleNextPage(data)
      return
    }
    const indexByPage = page * rows
    const questionsForm = handleAddNextQuestion(data.questions, indexByPage)

    const nextAnswerIndex = parseInt(activeIndex.toString()) + 1 + indexByPage
    const lastAnsweredQuestion = data?.questions?.length - 1
    // next page
    const nextPage = Math.floor(lastAnsweredQuestion / NUMBER_OF_DISPLAYED_QUESTIONS)
    const activeItemsInPage = nextPage >= page ? lastAnsweredQuestion % NUMBER_OF_DISPLAYED_QUESTIONS : lastAnsweredQuestion
    const nextTabIndex = nextAnswerIndex < data?.questions?.length ? activeItemsInPage : parseInt(activeIndex.toString()) + 1
    //@ts-ignore
    setActiveIndex(nextTabIndex)

    //@ts-ignore
    reset({
      questions: questionsForm
    })

    if (nextPage > page) {
      setQuestionFilter({
        ...questionFilter,
        page: nextPage,
        changeActiveIndex: false
      })
    }
    setIsBusy(false)
  }

  function renderTabs() {
    const panes = questions.map((question, index) => {
      return {
        menuItem: getMenuItem(question, index),
        render: () => <TabPane key={question.id}>
          <div style={{ padding: '30px 10px' }}>{!isBusy && renderQuestion(question)}</div>
        </TabPane>
      }
    })
    return <Tab
      activeIndex={activeIndex}
      onTabChange={(_, data) => setActiveIndex(data.activeIndex)}
      menu={{ className: 'wrapped' }}
      panes={panes}
    />
  }

  function renderActions() {
    return <>
      <El.PageHeaderActionButton type='submit' disabled={loadingQuestionsCount}>
        {t('Submit')}
      </El.PageHeaderActionButton>
    </>
  }

  return <Layout
    close={RouteTypes.SURVEY}
    segmented
    isBusy={loadingQuestionsCount}
    onSubmit={editForm && handleSubmit(onSubmit)}
    title={surveyData?.title}
    actions={editForm && renderActions()}
  >
    {questions.length > 0 && <StyledTabWrapper>
      {<Box
        header={<Progress
          //@ts-ignore
          percent={editForm ? 100 : questions.length >= 0 ? parseInt(indexOfLastCorrectAnswer / totalQuestionsNo * 100) : 0}
          indicating
          progress>
          {t('Progress')}</Progress>}
        fluid
        content={<Form
          as={editForm ? 'div' : Form}
          style={{ position: 'relative', minHeight: '300px' }}
          onSubmit={!editForm && handleSubmit(onSubmit)}
        >
          <Dimmer inverted active={isBusy}>
            <Loader active={isBusy} />
          </Dimmer>
          {renderTabs()}
          <div>
            <div style={{ textAlign: 'center', fontStyle: 'italic' }}>
              {t('Questions ({{current}} of {{total}})',
                {
                  current: parseInt(activeIndex.toString()) + 1 + (page * rows),
                  total: totalQuestionsNo
                })}
            </div>
            {!editForm && <Form.Button
              style={{ color: 'white', float: 'right' }}>{t('Submit')}
            </Form.Button>
            }
          </div>
        </Form>}
      />
      }
    </StyledTabWrapper>
    }
  </Layout >
}
export default RequestEntries